// ============================================================
// Voice control — shared types for the ear/brain/hands seams
// ============================================================
//
// The VoiceListener seam is what keeps the STT engine swappable: phase 1
// runs the Web Speech API, phase 2 swaps in a local whisper/moonshine
// utterance engine without touching the grammar or the dispatcher. See
// docs/plans/voice-control.md for the whole architecture.

/**
 * `dozing` is the Web Speech ear having stopped respawning after a stretch of
 * silence: nothing is wrong and nothing needs granting, the next touch
 * anywhere in the app brings it back. A quiet state, like `listening` — the
 * HUD does not expand over it.
 */
export type VoiceListenerState =
  | 'idle'
  | 'starting'
  | 'listening'
  | 'dozing'
  | 'error'

export interface VoiceListenerCallbacks {
  /** One discrete final utterance — a phrase the recognizer closed. */
  onUtterance: (text: string) => void
  /** Live not-yet-final text for the HUD ('' when cleared). */
  onInterim: (text: string) => void
  onStateChange: (state: VoiceListenerState, detail?: string) => void
  /** End-of-speech to transcript time, when the engine can measure it. */
  onLatency?: (roundTripMs: number) => void
}

export interface VoiceListener {
  readonly isSupported: boolean
  start: () => void
  stop: () => void
}

export interface VoiceCommandArgs {
  /** Parsed value of the first `<n>` slot when the phrase has one. */
  n?: number
  /** Parsed value of the second `<n>` slot ("loop from <n> to <n>"). */
  m?: number
}

export interface VoiceCommandFailure {
  failed: true
  /** Why nothing happened — surfaces in the HUD and as a toast. */
  message: string
}

/**
 * What `run` reports back: a string label (success feedback overriding
 * `label`), undefined (success, HUD falls back to `label`), or a failure
 * whose message tells the user the command did nothing and why — so they
 * know to fix the situation or just say it again.
 */
export type VoiceCommandResult = string | VoiceCommandFailure | undefined

export function voiceFailure(message: string): VoiceCommandFailure {
  return { failed: true, message }
}

export interface VoiceCommand {
  /** Stable id, e.g. 'transport.play'. */
  id: string
  /** Fallback HUD label when `run` returns nothing, e.g. 'Play'. */
  label: string
  /**
   * Spoken forms: lowercase words separated by single spaces, matching the
   * normalized utterance. `<n>` marks a numeric slot — digits ("15", "1.5")
   * and English number words ("twenty five") both fill it.
   */
  phrases: string[]
  /** When present and false, the command is skipped during matching. */
  available?: () => boolean
  /**
   * Matches even when wake-word mode would ignore the utterance. For the
   * few commands that must stay reachable while open-ended audio is being
   * captured — a listening stage's own cancel phrases. Use sparingly:
   * every exempt phrase is one more thing a lyric line could say.
   */
  ignoresWakeWord?: boolean
  /**
   * Perform the action. May return a feedback label that overrides `label`
   * (e.g. 'Speed 1.5x'), undefined to fall back to `label`, or a
   * `voiceFailure(...)` explaining why nothing happened.
   */
  run: (args: VoiceCommandArgs) => VoiceCommandResult
}

export interface VoiceMatch {
  command: VoiceCommand
  n?: number
  m?: number
  /** The exact phrase (with `<n>` literals) that consumed the utterance. */
  phrase?: string
}
